import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useComparison } from '../contexts/ComparisonContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import VerifiedBadge from '../components/VerifiedBadge';
import AuctionTimer from '../components/AuctionTimer';
import { maskPhone, maskEmail } from '../utils/contactUtils';
import { MapPin, Gauge, Calendar, Hash, AlertCircle, DollarSign, Star, Ship, Package, Heart, X, CheckCircle, GitCompare, MessageCircle, Mail, Phone, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolveImg = (url) => url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const VehicleDetail = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { connectToAuction } = useWebSocket();
  const { addVehicle, removeVehicle, isSelected } = useComparison();
  const { rate: USD_TO_NGN_RATE } = useExchangeRate();
  
  const [vehicle, setVehicle] = useState(null);
  const [dealerInfo, setDealerInfo] = useState(null);
  const [hasContactAccess, setHasContactAccess] = useState(false);
  const [contactVisibility, setContactVisibility] = useState({
    contact_access: 'public_summary',
    phone_visible: false,
    email_visible: false,
    whatsapp_visible: false,
    dealer_phone: null,
    dealer_email: null,
    dealer_whatsapp: null,
    locked_message: null
  });
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [bidAmount, setBidAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [bidding, setBidding] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [reserving, setReserving] = useState(false);
  const [reservationSuccess, setReservationSuccess] = useState(false);
  const [reservationError, setReservationError] = useState('');
  const [depositCap, setDepositCap] = useState(5000000); // Default ₦5M cap
  
  const inComparison = vehicle ? isSelected(vehicle.vehicle_id) : false;

  useEffect(() => {
    fetchVehicleData();
    checkWatchlistStatus();
    fetchDepositConfig();
  }, [id]);

  const fetchDepositConfig = async () => {
    try {
      const response = await axios.get(`${API}/payments/config`);
      if (response.data.maximum_deposit_cap) {
        setDepositCap(response.data.maximum_deposit_cap);
      }
    } catch (error) {
      console.error('Error fetching deposit config:', error);
      // Keep default cap if fetch fails
    }
  };

  const checkWatchlistStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/watchlist`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      const watchlistVehicles = response.data.vehicles || [];
      const isInWatchlist = watchlistVehicles.some(v => v.vehicle_id === id);
      setIsWatchlisted(isInWatchlist);
    } catch (error) {
      console.error('Error checking watchlist:', error);
    }
  };
  
  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const toggleWatchlist = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login to add to watchlist');
        navigate('/login');
        return;
      }

      if (isWatchlisted) {
        await axios.delete(`${API}/watchlist/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setIsWatchlisted(false);
      } else {
        await axios.post(`${API}/watchlist/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setIsWatchlisted(true);
      }
    } catch (error) {
      console.error('Watchlist error:', error);
      alert('Failed to update watchlist');
    }
  };

  const toggleComparison = () => {
    if (!vehicle) return;
    
    if (inComparison) {
      removeVehicle(vehicle.vehicle_id);
      toast.success('Removed from comparison');
    } else {
      const result = addVehicle(vehicle);
      if (result.success) {
        toast.success('Added to comparison');
      } else {
        toast.error(result.message);
      }
    }
  };

  const handleWhatsAppContact = () => {
    if (dealerInfo?.whatsapp && vehicle) {
      const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
      const message = encodeURIComponent(`Hello, I'm interested in ${vehicleName} on JAMOH & CO AUTOS LTD`);
      
      // If whatsapp is a link, use it directly
      if (dealerInfo.whatsapp.startsWith('http')) {
        window.open(dealerInfo.whatsapp, '_blank');
      } else {
        // If it's a phone number, create wa.me link
        const phoneNumber = dealerInfo.whatsapp.replace(/[^0-9+]/g, '');
        window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
      }
    }
  };

  const handleReservationAndPayment = async () => {
    setReserving(true);
    setReservationError('');

    // Compute NGN equivalent based on vehicle's currency
    const vehiclePriceRaw = vehicle?.price || 0;
    const isNgnVehicle = vehicle?.currency === 'NGN';
    const vehiclePriceNGN = isNgnVehicle ? vehiclePriceRaw : vehiclePriceRaw * USD_TO_NGN_RATE;
    const standardDepositNGN = vehiclePriceNGN * 0.1;
    const minDepositNGN = Math.min(standardDepositNGN, depositCap);
    const amount = parseFloat(depositAmount);

    if (!depositAmount || isNaN(amount)) {
      setReservationError('Please enter a deposit amount');
      setReserving(false);
      return;
    }

    if (amount < minDepositNGN) {
      setReservationError(`Deposit must be at least ₦${minDepositNGN.toLocaleString()}`);
      setReserving(false);
      return;
    }

    // Enforce deposit cap
    if (amount > depositCap) {
      setReservationError(`Online deposit is capped at ₦${depositCap.toLocaleString()}. Please use this amount.`);
      setReserving(false);
      return;
    }

    if (amount > vehiclePriceNGN) {
      setReservationError('Deposit cannot exceed vehicle price');
      setReserving(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let reservationId = null;
      
      // Step 1: Check if user already has an active reservation for this vehicle
      try {
        const reservationsResponse = await axios.get(
          `${API}/reservations`,
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }
        );

        const existingReservation = reservationsResponse.data.find(
          r => r.vehicle_id === id && ['pending', 'approved'].includes(r.status)
        );

        if (existingReservation) {
          // Check if payment already exists and is successful
          try {
            const paymentsResponse = await axios.get(
              `${API}/payments/my`,
              {
                headers: { Authorization: `Bearer ${token}` },
                withCredentials: true
              }
            );

            const existingPayment = paymentsResponse.data.find(
              p => p.reservation_id === existingReservation.reservation_id && p.status === 'successful'
            );

            if (existingPayment) {
              setReservationError('You have already paid for this reservation. Check your dashboard.');
              setReserving(false);
              return;
            }
          } catch (error) {
            // No existing payment found
          }

          // Use existing reservation
          reservationId = existingReservation.reservation_id;
        }
      } catch (error) {
        // No existing reservation found
      }

      // Step 2: Create new reservation if none exists
      if (!reservationId) {
        const reservationResponse = await axios.post(
          `${API}/reservations`,
          { vehicle_id: id },
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }
        );

        reservationId = reservationResponse.data.reservation_id;
      }

      // Step 3: Initialize payment
      const paymentResponse = await axios.post(
        `${API}/payments/initialize`,
        {
          reservation_id: reservationId,
          amount: amount
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      const { authorization_url, reference, access_code } = paymentResponse.data;

      // Step 4: Open Paystack popup
      const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
      
      if (!PAYSTACK_PUBLIC_KEY || PAYSTACK_PUBLIC_KEY === 'pk_test_your_paystack_public_key_here') {
        setReservationError('Payment system not configured. Please contact support.');
        setReserving(false);
        return;
      }

      const PaystackPop = window.PaystackPop;
      
      if (!PaystackPop) {
        // Fallback: Redirect to Paystack authorization URL
        if (authorization_url) {
          window.location.href = authorization_url;
        } else {
          setReservationError('Payment URL not available. Please try again.');
          setReserving(false);
        }
        return;
      }

      // Try to open Paystack popup
      try {
        const handler = PaystackPop.setup({
          key: PAYSTACK_PUBLIC_KEY,
          email: user.email,
          amount: amount * 100, // Convert to kobo
          currency: 'NGN',
          ref: reference,
          callback: async (response) => {
            // Verify payment
            try {
              const verifyResponse = await axios.get(
                `${API}/payments/verify/${response.reference}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  withCredentials: true
                }
              );

              setReservationSuccess(true);
              toast.success('Payment successful!');

              // Redirect to dashboard
              setTimeout(() => {
                setShowReservationModal(false);
                navigate('/dashboard');
              }, 2000);
            } catch (error) {
              console.error('Payment verification error:', error);
              setReservationError('Payment verification failed. Please contact support.');
              setReserving(false);
            }
          },
          onClose: () => {
            setReservationError('Payment cancelled');
            setReserving(false);
          }
        });

        handler.openIframe();
      } catch (popupError) {
        console.error('Paystack popup setup error:', popupError);
        
        // Fallback: Redirect to authorization URL
        if (authorization_url) {
          window.location.href = authorization_url;
        } else {
          setReservationError(`Payment window failed: ${popupError.message || 'Unknown error'}`);
          setReserving(false);
        }
      }

    } catch (error) {
      console.error('Reservation/Payment error:', error);
      
      // Improve error message handling
      let errorMessage = 'Failed to process payment. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 500) {
        errorMessage = 'Payment service is temporarily unavailable. Please try again in a moment.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.detail || 'Invalid payment request. Please check your deposit amount.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setReservationError(errorMessage);
      setReserving(false);
    }
  };

  useEffect(() => {
    if (auction) {
      fetchBids();
      
      const ws = connectToAuction(auction.auction_id, (message) => {
        if (message.type === 'new_bid') {
          setAuction(prev => ({ ...prev, current_bid: message.current_bid }));
          fetchBids();
        }
      });

      return () => ws?.close();
    }
  }, [auction?.auction_id]);

  const fetchVehicleData = async () => {
    try {
      const vehicleRes = await axios.get(`${API}/vehicles/${id}`);
      setVehicle(vehicleRes.data);
      
      // Fetch dealer info if dealer_id exists
      if (vehicleRes.data.dealer_id) {
        try {
          const dealerRes = await axios.get(`${API}/dealers/profile/${vehicleRes.data.dealer_id}`);
          setDealerInfo(dealerRes.data);
        } catch (error) {
          console.error('Error fetching dealer info:', error);
        }
      }
      
      // ALWAYS check contact access (not dependent on dealer_id)
      try {
        const accessRes = await axios.get(
          `${API}/vehicles/${id}/contact-access`,
          {
            headers: getHeaders(),
            withCredentials: true
          }
        );
        
        // Set contact visibility state from backend response
        setContactVisibility(accessRes.data);
        setHasContactAccess(accessRes.data.contact_access === 'unlocked' || accessRes.data.contact_access === 'internal');
        
        // Update dealer info with backend-provided contact data
        setDealerInfo(prev => ({
          ...prev,
          phone: accessRes.data.dealer_phone,
          email: accessRes.data.dealer_email,
          whatsapp: accessRes.data.dealer_whatsapp,
          name: accessRes.data.dealer_name
        }));
      } catch (error) {
        console.error('Contact access check error:', error.response?.status, error.response?.data);
        // On error, default to locked
        setContactVisibility({
          contact_access: 'public_summary',
          phone_visible: false,
          email_visible: false,
          whatsapp_visible: false,
          dealer_phone: null,
          dealer_email: null,
          dealer_whatsapp: null,
          locked_message: 'Contact details unlock after successful reservation deposit.'
        });
        setHasContactAccess(false);
      }
      
      // Only fetch auction for auction listings
      if (vehicleRes.data.listing_type !== 'direct_sale') {
        const auctionRes = await axios.get(`${API}/auctions/${id}/info`);
        if (auctionRes.data) {
          setAuction(auctionRes.data);
        }
      }
    } catch (error) {
      console.error('Error fetching vehicle:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async () => {
    if (!auction) return;
    try {
      const response = await axios.get(`${API}/auctions/${auction.auction_id}/bids`);
      setBids(response.data);
    } catch (error) {
      console.error('Error fetching bids:', error);
    }
  };

  const placeBid = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    const amount = parseFloat(bidAmount);
    if (!amount || amount <= auction.current_bid) {
      alert(`Bid must be higher than current bid of $${auction.current_bid}`);
      return;
    }

    setBidding(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auctions/${auction.auction_id}/bid`,
        { amount },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        }
      );
      setBidAmount('');
      alert('Bid placed successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to place bid');
    } finally {
      setBidding(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <p className="text-2xl text-textSecondary">Vehicle not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="text-textSecondary hover:text-primary mb-6 transition-colors"
        >
          ← Back to Marketplace
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images & Details */}
          <div className="lg:col-span-2">
            {/* Main Image */}
            <div className="bg-surface rounded-sm overflow-hidden mb-4">
              <img
                src={resolveImg(vehicle.images[selectedImage])}
                alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                className="w-full aspect-video object-cover"
                data-testid="vehicle-main-image"
              />
            </div>

            {/* Thumbnails */}
            <div className="grid grid-cols-4 gap-4 mb-8">
              {vehicle.images.map((image, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`aspect-video rounded-sm overflow-hidden border-2 ${
                    selectedImage === idx ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  <img src={resolveImg(image)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>

            {/* Vehicle Info */}
            <div className="glass p-6 rounded-sm mb-8">
              <div className="flex items-start justify-between mb-4">
                <h1 className="text-4xl font-heading font-bold flex-1" data-testid="vehicle-title">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </h1>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleComparison}
                    className={`ml-4 p-3 rounded-full transition-all ${
                      inComparison
                        ? 'bg-primary/20 hover:bg-primary/30'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                    title={inComparison ? 'Remove from comparison' : 'Add to comparison'}
                  >
                    <GitCompare
                      className={`w-6 h-6 transition-all ${
                        inComparison ? 'text-primary' : 'text-textSecondary'
                      }`}
                    />
                  </button>
                  <button
                    onClick={toggleWatchlist}
                    className={`p-3 rounded-full transition-all ${
                      isWatchlisted
                        ? 'bg-primary/20 hover:bg-primary/30'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                    title={isWatchlisted ? 'Remove from watchlist' : 'Add to watchlist'}
                  >
                    <Heart
                      className={`w-6 h-6 transition-all ${
                        isWatchlisted ? 'fill-primary text-primary' : 'text-textSecondary'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Dealer Info Section - Removed duplicate contact buttons, moved to "Seller Information & Rating" section below */}
              {dealerInfo && (
                <div className="mb-6 p-4 bg-surfaceHighlight rounded-sm border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs text-textSecondary mb-1">Sold by</p>
                      <Link 
                        to={`/dealer/${dealerInfo.dealer_id}`}
                        className="text-lg font-heading font-bold hover:text-primary transition-colors"
                      >
                        {dealerInfo.name}
                      </Link>
                    </div>
                    {dealerInfo.is_verified && (
                      <VerifiedBadge size="sm" variant="premium" />
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="flex items-center space-x-2">
                  <Gauge className="w-5 h-5 text-primary" />
                  <div>
                    <div className="text-xs text-textSecondary">Mileage</div>
                    <div className="font-bold">{vehicle.mileage.toLocaleString()} mi</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  <div>
                    <div className="text-xs text-textSecondary">Year</div>
                    <div className="font-bold">{vehicle.year}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Hash className="w-5 h-5 text-primary" />
                  <div>
                    <div className="text-xs text-textSecondary">VIN Number</div>
                    <div className="font-bold text-sm font-mono" title={vehicle.vin}>{vehicle.vin}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  <div>
                    <div className="text-xs text-textSecondary">Location</div>
                    <div className="font-bold text-xs">{vehicle.location}</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4 mb-6 flex-wrap">
                <span className={`px-4 py-2 rounded-sm text-sm font-bold uppercase ${
                  vehicle.condition === 'clean' ? 'bg-green-600' :
                  vehicle.condition === 'salvage' ? 'bg-yellow-600' : 'bg-red-600'
                }`}>
                  {vehicle.condition}
                </span>
                <span className={`px-4 py-2 rounded-sm text-sm font-bold uppercase ${
                  vehicle.listing_type === 'direct_sale' ? 'bg-blue-600' : 'bg-primary'
                }`}>
                  {vehicle.listing_type === 'direct_sale' ? 'For Sale' : 'Auction'}
                </span>
                <span className="px-4 py-2 bg-surfaceHighlight rounded-sm text-sm font-bold uppercase">
                  {vehicle.body_type}
                </span>
                {vehicle.transmission && (
                  <span className="px-4 py-2 bg-surfaceHighlight rounded-sm text-sm">
                    {vehicle.transmission}
                  </span>
                )}
                {vehicle.fuel_type && (
                  <span className="px-4 py-2 bg-surfaceHighlight rounded-sm text-sm">
                    {vehicle.fuel_type}
                  </span>
                )}
                {vehicle.color && (
                  <span className="px-4 py-2 bg-surfaceHighlight rounded-sm text-sm">
                    {vehicle.color}
                  </span>
                )}
                {vehicle.drivetrain && (
                  <span className="px-4 py-2 bg-surfaceHighlight rounded-sm text-sm">
                    {vehicle.drivetrain}
                  </span>
                )}
                {vehicle.damage_type && (
                  <span className="px-4 py-2 bg-red-600/20 text-red-500 rounded-sm text-sm font-bold uppercase flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>Damage: {vehicle.damage_type}</span>
                  </span>
                )}
              </div>

              {/* Shipping Estimate to Lagos */}
              <div className="border border-primary/30 rounded-sm p-5 mb-6 bg-surfaceHighlight">
                <h3 className="font-heading font-bold text-lg mb-3 flex items-center space-x-2">
                  <Ship className="w-5 h-5 text-primary" />
                  <span>Shipping to Lagos, Nigeria</span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-3">
                  <div>
                    <div className="text-textSecondary text-xs mb-1">Ocean Freight</div>
                    <div className="font-bold text-primary text-lg">$1,500</div>
                  </div>
                  <div>
                    <div className="text-textSecondary text-xs mb-1">Delivery Time</div>
                    <div className="font-bold text-lg">4-6 weeks</div>
                  </div>
                  <div>
                    <div className="text-textSecondary text-xs mb-1">Port of Arrival</div>
                    <div className="font-bold text-lg">Lagos</div>
                  </div>
                </div>
                <div className="bg-surface p-3 rounded-sm">
                  <p className="text-xs text-textSecondary leading-relaxed">
                    <strong className="text-primary">Important:</strong> Shipping cost includes ocean freight from US port to Lagos. 
                    Nigeria customs duty (35% of vehicle value) and clearing fees (10%) apply separately. 
                    Use our <a href="/calculator" className="text-primary underline">Import Calculator</a> for full cost breakdown.
                  </p>
                </div>
              </div>

              {/* Condition Report */}
              <div className="border-t border-white/10 pt-6 mb-6">
                <h2 className="text-xl font-heading font-bold mb-4 flex items-center space-x-2">
                  <Package className="w-5 h-5 text-primary" />
                  <span>Condition Report</span>
                </h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-surfaceHighlight p-4 rounded-sm">
                    <div className="text-textSecondary mb-1">Overall Condition</div>
                    <div className={`font-bold text-lg uppercase ${
                      vehicle.condition === 'clean' ? 'text-green-500' :
                      vehicle.condition === 'salvage' ? 'text-yellow-500' : 'text-red-500'
                    }`}>{vehicle.condition}</div>
                  </div>
                  <div className="bg-surfaceHighlight p-4 rounded-sm">
                    <div className="text-textSecondary mb-1">Damage Type</div>
                    <div className="font-bold text-lg">{vehicle.damage_type || 'None'}</div>
                  </div>
                  <div className="bg-surfaceHighlight p-4 rounded-sm">
                    <div className="text-textSecondary mb-1">Engine Size</div>
                    <div className="font-bold text-lg">{vehicle.engine_size || 'N/A'}</div>
                  </div>
                  <div className="bg-surfaceHighlight p-4 rounded-sm">
                    <div className="text-textSecondary mb-1">Mileage</div>
                    <div className="font-bold text-lg">{vehicle.mileage.toLocaleString()} mi</div>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-6">
                <h2 className="text-xl font-heading font-bold mb-3">Description</h2>
                <p className="text-textSecondary leading-relaxed">{vehicle.description}</p>
              </div>

              <div className="border-t border-white/10 pt-6 mt-6">
                <h2 className="text-xl font-heading font-bold mb-4">Seller Information & Rating</h2>
                
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-bold">
                      {contactVisibility.dealer_name ? contactVisibility.dealer_name[0] : vehicle.seller_name[0]}
                    </span>
                  </div>
                  <div className="flex-1">
                    {/* Clickable Seller Name */}
                    <Link 
                      to={`/dealer/${contactVisibility.dealer_id || vehicle.seller_id}`}
                      className="font-bold text-lg mb-2 hover:text-primary transition-colors inline-block"
                    >
                      {contactVisibility.dealer_name || vehicle.seller_name}
                    </Link>
                    
                    {/* Rating */}
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className="w-5 h-5 text-yellow-500 fill-current" />
                        ))}
                      </div>
                      <span className="text-sm font-bold">5.0</span>
                      <span className="text-sm text-textSecondary">(248 reviews)</span>
                    </div>
                    
                    {/* Verified Badge & Member Since */}
                    <div className="flex items-center space-x-2">
                      {dealerInfo?.is_verified && (
                        <span className="px-3 py-1 bg-green-600 rounded-sm text-xs font-bold uppercase">Verified Dealer</span>
                      )}
                      <span className="text-xs text-textSecondary">Member since 2022</span>
                    </div>
                    
                    {/* Dealer Summary */}
                    <div className="mt-3 text-sm text-textSecondary">
                      <p>Professional dealer with excellent customer feedback. Fast shipping, accurate descriptions, responsive communication.</p>
                    </div>
                  </div>
                </div>

                {/* Contact Visibility - Backend Driven */}
                {contactVisibility.contact_access === 'unlocked' || contactVisibility.contact_access === 'internal' ? (
                  /* UNLOCKED: Show Full Contact Details */
                  <div className="mt-4 space-y-3">
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                      {contactVisibility.whatsapp_visible && contactVisibility.dealer_whatsapp && (
                        <button
                          onClick={handleWhatsAppContact}
                          className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white rounded-sm font-bold px-6 py-2.5 text-sm transition-colors flex-1 min-w-[150px]"
                        >
                          <MessageCircle className="w-4 h-4" />
                          <span>WhatsApp</span>
                        </button>
                      )}
                      {contactVisibility.phone_visible && contactVisibility.dealer_phone && (
                        <a
                          href={`tel:${contactVisibility.dealer_phone}`}
                          className="flex items-center justify-center space-x-2 glass hover:bg-white/10 text-white rounded-sm font-bold px-6 py-2.5 text-sm transition-colors border border-white/20 flex-1 min-w-[150px]"
                        >
                          <Phone className="w-4 h-4" />
                          <span>Call</span>
                        </a>
                      )}
                      {contactVisibility.email_visible && contactVisibility.dealer_email && (
                        <a
                          href={`mailto:${contactVisibility.dealer_email}`}
                          className="flex items-center justify-center space-x-2 glass hover:bg-white/10 text-white rounded-sm font-bold px-6 py-2.5 text-sm transition-colors border border-white/20 flex-1 min-w-[150px]"
                        >
                          <Mail className="w-4 h-4" />
                          <span>Email</span>
                        </a>
                      )}
                    </div>
                    
                    {/* Full Contact Details */}
                    <div className="p-3 bg-surfaceHighlight rounded-sm border border-white/10">
                      {contactVisibility.phone_visible && contactVisibility.dealer_phone && (
                        <p className="text-sm text-textSecondary mb-1">
                          <span className="font-medium text-white">Phone:</span> {contactVisibility.dealer_phone}
                        </p>
                      )}
                      {contactVisibility.email_visible && contactVisibility.dealer_email && (
                        <p className="text-sm text-textSecondary">
                          <span className="font-medium text-white">Email:</span> {contactVisibility.dealer_email}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  /* LOCKED: Show Lock Message */
                  <div className="mt-4 p-4 bg-yellow-900/20 border border-yellow-600 rounded-sm">
                    <div className="flex items-start space-x-3">
                      <Lock className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-400 mb-1">
                          Contact Details Locked
                        </p>
                        <p className="text-xs text-yellow-300">
                          {contactVisibility.locked_message || 'Contact details unlock after successful reservation deposit.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Pricing & Auction Info */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">

              {/* Direct Sale Panel */}
              {vehicle.listing_type === 'direct_sale' && (
                <div className="glass p-6 rounded-sm">
                  <div className="text-center mb-6">
                    <span className="inline-block px-4 py-1 bg-blue-600 rounded-sm text-sm font-bold uppercase mb-4">For Sale</span>
                    <div className="text-sm text-textSecondary uppercase tracking-wider mb-2">Sale Price</div>
                    {vehicle.currency === 'NGN' ? (
                      <div className="text-5xl font-heading font-bold text-primary" data-testid="sale-price">
                        ₦{vehicle.price?.toLocaleString()}
                      </div>
                    ) : (
                      <>
                        <div className="text-5xl font-heading font-bold text-primary" data-testid="sale-price">
                          ${vehicle.price?.toLocaleString()}
                        </div>
                        <div className="text-sm text-textSecondary mt-2">
                          ≈ ₦{((vehicle.price || 0) * USD_TO_NGN_RATE).toLocaleString()}
                        </div>
                        <div className="text-xs text-textSecondary/70 mt-1">
                          @ ₦{USD_TO_NGN_RATE.toLocaleString()}/USD
                        </div>
                      </>
                    )}
                  </div>
                  {!authLoading && user ? (
                    <button
                      onClick={() => setShowReservationModal(true)}
                      data-testid="buy-now-direct-button"
                      className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase py-3 transition-all duration-300 red-glow"
                    >
                      Buy Now
                    </button>
                  ) : authLoading ? (
                    <div className="text-center py-4">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate('/login')}
                      className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase py-3 transition-all duration-300 red-glow"
                    >
                      Login to Buy
                    </button>
                  )}
                </div>
              )}

              {/* Auction Timer */}
              {vehicle.listing_type !== 'direct_sale' && auction && <AuctionTimer endTime={auction.end_time} />}

              {/* Current Bid */}
              {vehicle.listing_type !== 'direct_sale' && auction && (
                <div className="glass p-6 rounded-sm">
                  <div className="text-center mb-6">
                    <div className="text-sm text-textSecondary uppercase tracking-wider mb-2">Current Bid</div>
                    <div className="text-5xl font-heading font-bold text-primary" data-testid="current-bid">
                      ${auction.current_bid.toLocaleString()}
                    </div>
                    <div className="text-sm text-textSecondary mt-2">{auction.bid_count} bids placed</div>
                  </div>

                  {auction.buy_now_price && (
                    <div className="border-t border-white/10 pt-4 mb-6">
                      <div className="flex items-center justify-between">
                        <span className="text-textSecondary">Buy Now Price:</span>
                        <span className="text-2xl font-heading font-bold">${auction.buy_now_price.toLocaleString()}</span>
                      </div>
                    </div>
                  )}

                  {/* Bid Form */}
                  {!authLoading && user ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Your Bid Amount</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                          <input
                            type="number"
                            value={bidAmount}
                            onChange={(e) => setBidAmount(e.target.value)}
                            placeholder={`Min: ${auction.current_bid + 100}`}
                            data-testid="bid-amount-input"
                            className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary"
                          />
                        </div>
                      </div>
                      <button
                        onClick={placeBid}
                        disabled={bidding || !bidAmount}
                        data-testid="place-bid-button"
                        className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase py-3 transition-all duration-300 red-glow disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {bidding ? 'Placing Bid...' : 'Place Bid'}
                      </button>
                    </div>
                  ) : authLoading ? (
                    <div className="text-center py-4">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate('/login')}
                      className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase py-3 transition-all duration-300 red-glow"
                    >
                      Login to Bid
                    </button>
                  )}
                </div>
              )}

              {/* Bid History */}
              {vehicle.listing_type !== 'direct_sale' && auction && bids.length > 0 && (
                <div className="glass p-6 rounded-sm">
                  <h2 className="text-xl font-heading font-bold mb-4">Bid History</h2>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {bids.map((bid, idx) => (
                      <div
                        key={bid.bid_id}
                        className="flex items-center justify-between p-3 bg-surfaceHighlight rounded-sm"
                        data-testid={`bid-history-item-${idx}`}
                      >
                        <div>
                          <div className="font-bold">{bid.user_name}</div>
                          <div className="text-xs text-textSecondary">
                            {new Date(bid.timestamp).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-xl font-heading font-bold text-primary">
                          ${bid.amount.toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reservation Modal */}
      {showReservationModal && vehicle && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => !reserving && !reservationSuccess && setShowReservationModal(false)}>
          <div className="glass max-w-lg w-full rounded-sm max-h-[80vh] overflow-y-auto animate-fadeIn" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
            {reservationSuccess ? (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-heading font-bold mb-2">Payment Successful!</h2>
                <p className="text-textSecondary mb-4">Redirecting to your dashboard...</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-heading font-bold flex items-center gap-2">
                    <DollarSign className="w-6 h-6 text-primary" />
                    Reserve & Pay Deposit
                  </h2>
                  <button 
                    onClick={() => setShowReservationModal(false)} 
                    disabled={reserving}
                    className="text-textSecondary hover:text-white disabled:opacity-50"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Vehicle Info */}
                <div className="bg-surfaceHighlight p-4 rounded-sm mb-6">
                  <div className="text-sm text-textSecondary mb-1">Vehicle</div>
                  <div className="font-heading font-bold text-xl mb-3">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </div>
                  <div className="space-y-2">
                    {vehicle.currency === 'NGN' ? (
                      <div className="flex justify-between items-center">
                        <span className="text-textSecondary">List Price:</span>
                        <span className="font-bold text-xl text-primary">
                          ₦{vehicle.price?.toLocaleString() || 'N/A'}
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-textSecondary">List Price (USD):</span>
                          <span className="font-bold text-lg">
                            ${vehicle.price?.toLocaleString() || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-white/10">
                          <span className="text-textSecondary">Payment Amount (NGN):</span>
                          <span className="font-bold text-xl text-primary">
                            ₦{((vehicle.price || 0) * USD_TO_NGN_RATE).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-xs text-textSecondary text-center pt-1">
                          Exchange Rate: $1 = ₦{USD_TO_NGN_RATE.toLocaleString()}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Deposit Explanation */}
                <div className="bg-blue-900/20 border border-blue-600 p-4 rounded-sm mb-6">
                  <h4 className="text-sm font-bold text-blue-400 mb-2 flex items-center space-x-2">
                    <DollarSign className="w-4 h-4" />
                    <span>About Your Deposit</span>
                  </h4>
                  <p className="text-xs text-blue-300 mb-3 leading-relaxed">
                    Deposit secures this vehicle and confirms your intent to purchase. This amount is part of the total vehicle cost and will be deducted from the final payment.
                  </p>
                  <div className="bg-yellow-900/20 border border-yellow-600 p-3 rounded-sm">
                    <p className="text-xs text-yellow-400 font-medium">
                      ⚠️ Important: Deposits are non-refundable once payment is made.
                    </p>
                  </div>
                </div>

                {/* Payment Section */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Deposit Amount (NGN) <span className="text-primary">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-textSecondary font-bold">₦</span>
                      <input
                        type="number"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        placeholder={(() => {
                          const priceNGN = vehicle.currency === 'NGN' ? (vehicle.price || 0) : (vehicle.price || 0) * USD_TO_NGN_RATE;
                          const standardDeposit = priceNGN * 0.1;
                          const cappedDeposit = Math.min(standardDeposit, depositCap);
                          return cappedDeposit.toLocaleString();
                        })()}
                        disabled={reserving}
                        className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-10 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
                      />
                    </div>
                    <div className="mt-2 text-xs space-y-1">
                      {(() => {
                        const vehiclePriceNGN = vehicle.currency === 'NGN' ? (vehicle.price || 0) : (vehicle.price || 0) * USD_TO_NGN_RATE;
                        const standardDeposit = vehiclePriceNGN * 0.1;
                        const isCapApplied = standardDeposit > depositCap;
                        const minDeposit = Math.min(standardDeposit, depositCap);
                        
                        return (
                          <>
                            <div className="flex justify-between items-center">
                              <span className="text-textSecondary">
                                Required Deposit:
                              </span>
                              <span className="font-bold text-primary">
                                ₦{minDeposit.toLocaleString()}
                              </span>
                            </div>
                            {isCapApplied && (
                              <div className="bg-yellow-900/20 border border-yellow-600 rounded-sm p-3 mt-2">
                                <p className="text-yellow-400 font-medium flex items-center gap-2">
                                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                  <span>For high-value vehicles, online deposit is capped at ₦{depositCap.toLocaleString()}.</span>
                                </p>
                              </div>
                            )}
                            {!isCapApplied && (
                              <div className="flex justify-between items-center text-textSecondary">
                                <span>Standard 10% of vehicle price</span>
                                <span>Currency: <strong className="text-primary">NGN</strong></span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="bg-blue-900/20 border border-blue-500 rounded-sm p-3">
                    <p className="text-sm text-blue-200">
                      <strong>Secure Payment:</strong> You'll be redirected to Paystack's secure checkout to complete your deposit payment.
                    </p>
                  </div>

                  {reservationError && (
                    <div className="bg-red-900/20 border border-red-500 rounded-sm p-3 flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-200">{reservationError}</p>
                    </div>
                  )}
                </div>
                
                <div className="flex space-x-4">
                  <button
                    onClick={() => setShowReservationModal(false)}
                    disabled={reserving}
                    className="flex-1 bg-white/5 hover:bg-white/10 text-white rounded-sm font-bold py-3 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReservationAndPayment}
                    disabled={reserving || !depositAmount}
                    className="flex-1 bg-primary hover:bg-primaryHover text-white rounded-sm font-bold py-3 transition-all duration-300 red-glow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reserving ? 'Processing...' : 'Pay Now'}
                  </button>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetail;